import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/apiClient";
import { ExpertCard } from "../components/ExpertCard";
import { Star, Lock, Users, Sparkles } from "lucide-react";
import { useT } from "../lib/i18n";

const PACKS = [
  { t: "Bakery Tax Optimization Pack", d: "VAT, deductions, jurisdiction-specific tax planning for bakeries.", tag: "TAX" },
  { t: "SME CFO Review Pack", d: "Cash flow + working capital + profitability review.", tag: "FINANCE" },
  { t: "SAP S/4HANA OTC Process Review", d: "Order-to-Cash gap analysis with SAP best practice fit.", tag: "ERP" },
  { t: "Working Capital Diagnostic", d: "DSO / DIO / DPO benchmark and improvement plan.", tag: "FINANCE" },
  { t: "Vendor Transition Assessment", d: "Switching cost, risk and TCO across vendor candidates.", tag: "PROCUREMENT" },
  { t: "Pricing Strategy Review", d: "Elasticity, packaging and value-based pricing review.", tag: "PRICING" },
];

const selectCls = "border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:border-[#0EA5E9] focus:outline-none";

export default function Marketplace() {
  const { t } = useT();
  const [tab, setTab] = useState("experts");
  const [experts, setExperts] = useState(null);
  const [tax, setTax] = useState(null);
  const [filters, setFilters] = useState({ expertise: "", industry: "", availability: "" });

  useEffect(() => {
    api.get("/consultants/taxonomies").then((r) => setTax(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    api.get(`/experts${params ? `?${params}` : ""}`).then((r) => setExperts(r.data.experts)).catch(() => setExperts([]));
  }, [filters]);

  return (
    <DashboardLayout title={t("Marketplace")}
      action={
        <Link data-testid="become-expert-link" to="/become-expert" className="bg-[#0B1B3D] text-white hover:bg-[#1E3A8A] px-4 py-2.5 rounded-lg font-bold text-sm font-display inline-flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> {t("Become a Konzenzo Expert")}
        </Link>
      }>
      <div className="flex items-center gap-2 mb-6">
        <button data-testid="marketplace-tab-experts" onClick={() => setTab("experts")} className={`px-4 py-2 rounded-lg text-sm font-bold font-display inline-flex items-center gap-2 ${tab === "experts" ? "bg-[#0B1B3D] text-white" : "bg-white border border-slate-200 text-[#475569]"}`}>
          <Users className="w-4 h-4" /> {t("Human Experts")}
        </button>
        <button data-testid="marketplace-tab-packs" onClick={() => setTab("packs")} className={`px-4 py-2 rounded-lg text-sm font-bold font-display inline-flex items-center gap-2 ${tab === "packs" ? "bg-[#0B1B3D] text-white" : "bg-white border border-slate-200 text-[#475569]"}`}>
          <Star className="w-4 h-4" /> {t("Agent Packs")}
        </button>
      </div>

      {tab === "experts" && (
        <>
          <p className="text-[#475569] max-w-3xl">{t("Independent human consultants who validate your Konzenzo Decision Reports. AI analyzes the decision first — human expertise validates it when needed.")}</p>
          {tax && (
            <div className="flex flex-wrap gap-2 mt-5">
              <select data-testid="filter-expertise" className={selectCls} value={filters.expertise} onChange={(e) => setFilters((f) => ({ ...f, expertise: e.target.value }))}>
                <option value="">{t("All expertise")}</option>
                {Object.entries(tax.expertise_taxonomy).map(([g, items]) => (
                  <optgroup key={g} label={g}>{items.map((e) => <option key={e} value={e}>{tax.expertise_labels[e]}</option>)}</optgroup>
                ))}
              </select>
              <select data-testid="filter-industry" className={selectCls} value={filters.industry} onChange={(e) => setFilters((f) => ({ ...f, industry: e.target.value }))}>
                <option value="">{t("All industries")}</option>
                {tax.industries.map((i) => <option key={i}>{i}</option>)}
              </select>
              <select data-testid="filter-availability" className={selectCls} value={filters.availability} onChange={(e) => setFilters((f) => ({ ...f, availability: e.target.value }))}>
                <option value="">{t("Any availability")}</option>
                {tax.availability_options.map((a) => <option key={a} value={a}>{t(a)}</option>)}
              </select>
            </div>
          )}
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="experts-grid">
            {experts === null && <p className="text-[#94A3B8]">{t("Loading…")}</p>}
            {experts?.length === 0 && (
              <div data-testid="experts-empty" className="md:col-span-3 bg-white border border-dashed border-slate-200 rounded-xl p-10 text-center">
                <Users className="w-8 h-8 mx-auto text-[#0EA5E9]" />
                <p className="mt-3 font-display font-bold text-[#0B1B3D]">{t("No experts match these filters yet")}</p>
                <p className="text-sm text-[#475569] mt-1">{t("Are you a consultant?")} <Link to="/become-expert" className="text-[#0EA5E9] font-bold">{t("Become a Konzenzo Expert")}</Link></p>
              </div>
            )}
            {(experts || []).map((e) => <ExpertCard key={e.consultant_id} expert={e} labels={tax?.expertise_labels || {}} />)}
          </div>
        </>
      )}

      {tab === "packs" && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="marketplace-grid">
          {PACKS.map((p) => (
            <div key={p.t} className="relative bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <span className="font-mono-kz text-xs tracking-widest text-[#0EA5E9] font-bold">{p.tag}</span>
                <Lock className="w-4 h-4 text-[#94A3B8]" />
              </div>
              <p className="mt-4 font-display font-extrabold text-[#0B1B3D] text-lg leading-tight">{p.t}</p>
              <p className="mt-2 text-sm text-[#475569]">{p.d}</p>
              <button disabled className="mt-5 w-full px-4 py-2.5 rounded-lg font-bold text-sm bg-slate-100 text-[#94A3B8] cursor-not-allowed font-display">{t("Coming soon")}</button>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
